import { Reducer } from 'react';
import { ContextState } from 'src/client/contexts/recruitments/RecruitmentList';

export type Action =
  | {
      type: 'resetSearchQueryParams';
    }
  | {
      type: 'changeDate';
      payload: {
        date: ContextState['searchQueryParams']['date'];
      };
    }
  | {
      type: 'changeAreaIds';
      payload: {
        areaIds: ContextState['searchQueryParams']['areaIds'];
      };
    }
  | {
      type: 'changeLicenseIds';
      payload: {
        licenseIds: ContextState['searchQueryParams']['licenseIds'];
      };
    }
  | {
      type: 'selectFacilityFeatureIds';
      payload: {
        facilityFeatureIds: ContextState['searchQueryParams']['facilityFeatureIds'];
      };
    }
  | {
      type: 'changeJobBenefitIds';
      payload: {
        jobBenefitIds: ContextState['searchQueryParams']['jobBenefitIds'];
      };
    }
  | {
      type: 'changeWorkingHourOptionIds';
      payload: {
        workingHourOptionId: string;
      };
    }
  | {
      type: 'selectSortOptionId';
      payload: {
        sortOptionId: string;
      };
    }
  | {
      type: 'clearSortOptionId';
    }
  | {
      type: 'changeMaximumDailyWage';
      payload: {
        maximumDailyWage: ContextState['searchQueryParams']['maximumDailyWage'];
      };
    }
  | {
      type: 'changeMinimumDailyWage';
      payload: {
        minimumDailyWage: ContextState['searchQueryParams']['minimumDailyWage'];
      };
    }
  | {
      type: 'clearWorkingHourOptionIds';
    }
  | {
      type: 'changeDisplayMode';
      payload: {
        displayMode: ContextState['ui']['displayMode'];
      };
    }
  | {
      type: 'changeMaximumWorkingHours';
      payload: {
        maximumWorkingHours: ContextState['searchQueryParams']['maximumWorkingHours'];
      };
    }
  | {
      type: 'changeJobTaskIds';
      payload: {
        jobTaskIds: ContextState['searchQueryParams']['jobTaskIds'];
      };
    }
  | {
      type: 'changeOnlyRecruiting';
      payload: {
        onlyRecruiting: ContextState['searchQueryParams']['onlyRecruiting'];
      };
    };

export const recruitmentListReducer: Reducer<ContextState, Action> = (state, action) => {
  switch (action.type) {
    case 'resetSearchQueryParams': {
      return {
        ...state,
        // 検索の絞り込み条件だけをリセットしたいのであって、日付までリセットしてはいけない
        searchQueryParams: {
          date: state.searchQueryParams.date,
          areaIds: null,
          licenseIds: null,
          jobBenefitIds: null,
          workingHourOptionIds: null,
          maximumWorkingHours: null,
          jobTaskIds: null,
          maximumDailyWage: null,
          minimumDailyWage: null,
          facilityFeatureIds: null,
          sortOptionId: null,
          onlyRecruiting: null,
        },
      };
    }
    case 'changeDate': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          date: action.payload.date,
        },
      };
    }
    case 'changeAreaIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          areaIds: action.payload.areaIds,
        },
      };
    }
    case 'changeLicenseIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          licenseIds: action.payload.licenseIds,
        },
      };
    }
    case 'selectFacilityFeatureIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          facilityFeatureIds: action.payload.facilityFeatureIds,
        },
      };
    }
    case 'changeJobBenefitIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          jobBenefitIds: action.payload.jobBenefitIds,
        },
      };
    }
    case 'changeWorkingHourOptionIds': {
      const { workingHourOptionId } = action.payload;

      if (state.searchQueryParams.workingHourOptionIds?.includes(workingHourOptionId)) {
        const newIds = state.searchQueryParams.workingHourOptionIds
          ?.filter((id) => id !== workingHourOptionId)
          .filter((id) => typeof id === 'string');
        return {
          ...state,
          searchQueryParams: {
            ...state.searchQueryParams,
            workingHourOptionIds: newIds,
          },
        };
      }

      if (state.searchQueryParams.workingHourOptionIds) {
        return {
          ...state,
          searchQueryParams: {
            ...state.searchQueryParams,
            workingHourOptionIds: [...state.searchQueryParams.workingHourOptionIds, workingHourOptionId],
          },
        };
      }

      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          workingHourOptionIds: [workingHourOptionId],
        },
      };
    }

    case 'clearWorkingHourOptionIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          workingHourOptionIds: null,
        },
      };
    }

    case 'selectSortOptionId': {
      const { sortOptionId } = action.payload;

      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          sortOptionId: state.searchQueryParams.sortOptionId === sortOptionId ? null : sortOptionId,
        },
      };
    }

    case 'clearSortOptionId': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          sortOptionId: null,
        },
      };
    }
    case 'changeMaximumDailyWage': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          maximumDailyWage: action.payload.maximumDailyWage,
        },
      };
    }
    case 'changeMinimumDailyWage': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          minimumDailyWage: action.payload.minimumDailyWage,
        },
      };
    }
    case 'changeDisplayMode': {
      const newModeHistory = state.ui.modeHistory;
      newModeHistory.push(action.payload.displayMode);

      return {
        ...state,
        ui: {
          ...state.ui,
          displayMode: action.payload.displayMode,
          modeHistory: newModeHistory,
        },
      };
    }
    case 'changeMaximumWorkingHours': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          maximumWorkingHours: action.payload.maximumWorkingHours,
        },
      };
    }
    case 'changeJobTaskIds': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          jobTaskIds: action.payload.jobTaskIds,
        },
      };
    }
    case 'changeOnlyRecruiting': {
      return {
        ...state,
        searchQueryParams: {
          ...state.searchQueryParams,
          onlyRecruiting: action.payload.onlyRecruiting,
        },
      };
    }
    default:
      throw new Error('Unexpected action type in RecruitingList reducer');
  }
};
